import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const InvoiceContext = createContext();

export const useInvoices = () => useContext(InvoiceContext);

export const InvoiceProvider = ({ children }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all invoices
  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/invoices/all/');
      if (response.data.success) {
        setInvoices(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setError('Failed to fetch invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Create new invoice
  const createInvoice = async (invoiceData) => {
    try {
      // Extract account_id from invoiceData - check multiple possible field names
      const accountId = invoiceData.account_id || 
                       invoiceData.account || 
                       invoiceData.customer?.id || 
                       invoiceData.id;
      
      if (!accountId) {
        console.log('InvoiceContext: Missing account ID in invoiceData:', invoiceData);
        return { 
          success: false, 
          message: 'Account ID is required to create invoice'
        };
      }
      
      console.log('InvoiceContext: Creating invoice with account ID:', accountId);
      const response = await api.post(`/invoice/${accountId}/create/`, invoiceData);
      if (response.data.success) {
        // Refresh invoices list after creation
        await fetchInvoices();
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Failed to create invoice' };
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error creating invoice' 
      };
    }
  };

  // Update invoice status (Mark as Paid, Cancel, etc.)
  const updateInvoiceStatus = async (invoiceId, statusData) => {
    try {
      const response = await api.patch(`/invoice/${invoiceId}/details/`, statusData);
      if (response.data.success) {
        // Refresh invoices list after status update
        await fetchInvoices();
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Failed to update invoice status' };
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error updating invoice status' 
      };
    }
  };

  // Share/Send invoice via email
  const shareInvoice = async (invoiceId) => {
    try {
      const response = await api.post(`/invoice/${invoiceId}/share/`);
      if (response.data.success) {
        // Refresh invoices list to update shared_on timestamp
        await fetchInvoices();
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Failed to send invoice' };
      }
    } catch (error) {
      console.error('Error sharing invoice:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error sending invoice' 
      };
    }
  };

  // Add payment to invoice
  const addPayment = async (invoiceId, paymentData) => {
    try {
      const response = await api.post(`/invoice/${invoiceId}/payments/`, paymentData);
      if (response.data.success) {
        // Refresh invoices list after payment
        await fetchInvoices();
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Failed to add payment' };
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error adding payment' 
      };
    }
  };

  // Get invoice details
  const getInvoiceDetails = async (invoiceId) => {
    try {
      const response = await api.get(`/invoice/${invoiceId}/details/`);
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Failed to fetch invoice details' };
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error fetching invoice details' 
      };
    }
  };

  // Get customers for invoice creation
  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers/');
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message || 'Failed to fetch customers' };
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error fetching customers' 
      };
    }
  };

  // Fetch invoices for a specific account
  const fetchAccountInvoices = async (accountId) => {
    try {
      console.log('InvoiceContext: Fetching invoices for account:', accountId);
      const response = await api.get(`/account/${accountId}/invoices/`);
      console.log('InvoiceContext: Raw API response:', response.data);
      
      // Handle different response formats
      let invoiceData = response.data;
      
      // If response has a 'data' property, use that
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        invoiceData = response.data.data;
      }
      // If response is directly an array
      else if (response.data && Array.isArray(response.data)) {
        invoiceData = response.data;
      }
      // If response has success property and data
      else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        invoiceData = response.data.data;
      }
      
      console.log('InvoiceContext: Processed invoice data:', invoiceData);
      
      if (invoiceData && Array.isArray(invoiceData)) {
        // Map the response fields to match frontend expectations
        const mappedInvoices = invoiceData.map(invoice => ({
          id: invoice.id,
          invoice_number: invoice.invoice_number,
          account_name: invoice.account_name,
          owner_email: invoice.owner_email,
          owner_name: invoice.owner_name,
          // Map invoice values
          total_amount: invoice.taxable_invoice_value,
          invoice_value: invoice.invoice_value,
          tax_amount: invoice.tax_amount,
          taxable_invoice_value: invoice.taxable_invoice_value,
          // Map status
          status: invoice.status,
          status_verbose: invoice.status_verbose,
          // Map dates
          created_at: invoice.created_at,
          invoice_date: invoice.invoice_date,
          due_date: invoice.due_date,
          // Map payment info
          total_paid_amount: invoice.total_paid_amount,
          due_amount: invoice.taxable_invoice_value - (invoice.total_paid_amount || 0),
          shared_on: invoice.shared_on,
          // Map discount
          discount_percentage: invoice.discount_percentage,
          discount_amount: invoice.discount_amount,
          // Map description
          description: invoice.description,
          // Map particulars
          particulars: invoice.particulars || [],
          // Map payment history
          payment_history: invoice.payment_history || [],
          // Map invoice URL
          invoice_url: invoice.invoice_url,
          // Map subscription info
          account_subscription: invoice.account_subscription || null
        }));
        
        console.log('InvoiceContext: Mapped invoices:', mappedInvoices);
        return { success: true, data: mappedInvoices };
      } else {
        console.error('InvoiceContext: Invalid response format:', response.data);
        return { success: false, message: 'Invalid response format', rawData: response.data };
      }
    } catch (error) {
      console.error('Error fetching account invoices:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error fetching account invoices' 
      };
    }
  };

  // Export invoices (placeholder - backend endpoint not implemented)
  const exportInvoices = async (filters = {}) => {
    // TODO: Implement export endpoint in backend
    return { 
      success: false, 
      message: 'Export functionality not yet implemented' 
    };
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const value = {
    // State
    invoices,
    loading,
    error,
    
    // Actions
    fetchInvoices,
    createInvoice,
    updateInvoiceStatus,
    shareInvoice,
    addPayment,
    getInvoiceDetails,
    fetchCustomers,
    fetchAccountInvoices,
    exportInvoices
  };

  return (
    <InvoiceContext.Provider value={value}>
      {children}
    </InvoiceContext.Provider>
  );
};
